(ns machtfit.app.routes
  (:require [clojure.set :as set]
            [clojure.string :as str]
            [goog.Uri]
            [machtfit.app.tracking :as tracking]
            [machtfit.errors :as error]
            [machtfit.format :as format]
            [machtfit.init :as-alias init]
            [machtfit.startpage.events :as-alias events]
            [machtfit.startpage.global-search :as global-search]
            [machtfit.startpage.scrolling :as scrolling]
            [machtfit.startpage.statics :as statics]
            [machtfit.stepchall.add-activity :as-alias stepchall.add-activity]
            [machtfit.stepchall.create-team :as-alias stepchall.create-team]
            [machtfit.stepchall.join-team :as-alias stepchall.join-team]
            [machtfit.stepchall.leaderboard :as-alias stepchall.leaderboard]
            [machtfit.stepchall.my-steps :as-alias stepchall.my-steps]
            [machtfit.stepchall.my-team :as-alias stepchall.my-team]
            [machtfit.stepchall.route-view :as-alias stepchall.route-view]
            [machtfit.url-params :as url-params]
            [re-frame.core :as rf]
            [reitit.coercion]
            [reitit.coercion.malli]
            [reitit.core :as reitit]
            [reitit.frontend])
  (:require-macros [machtfit.app.routes :as route-macros]))

(defn change-url [href]
  (let [current-path-and-query (str js/location.pathname js/location.search)]
    (when-not (= href current-path-and-query)
      (js/window.history.pushState "" "" href))))

(rf/reg-fx ::change-url-and-history
  (fn [href]
    (js/window.history.pushState "" "" href)))

(rf/reg-fx ::load-page
  (fn [href]
    (js/location.assign href)))

(defn- renders-path? [router path]
  (when-let [match (reitit.frontend/match-by-path router path)]
    (not= [] (get-in match [:data :event]))))

(rf/reg-fx ::set-title-fx
  (fn [new-title]
    (set! (.-title js/document) new-title)))

(rf/reg-event-fx ::set-title
  (fn [_ [_ new-title]]
    {::set-title-fx new-title}))

(defn adapt-legacy-filter-query [query-string]
  (str/replace query-string "[]=" "="))

(defn ensure-trailing-slash [url]
  (str/replace url #"([^/]$)" "$1/"))

(defn match-coerced [router path]
  (try (reitit.frontend/match-by-path router path)
       (catch js/Error e
         (if (= (.-message e) "Request coercion failed")
           (error/capture-with-sentry "ReititCoercionError"
                                      (str "coercion failed for path " path)
                                      (.-data e))
           (throw e))
         ::coercion-failed)))

(def max-on-visit-retries 100)

(rf/reg-event-fx ::on-visit
  (fn [{:keys [db]} [_ match debounce-key {:keys [ready? event event-from-match-and-db] :as data} retries]]
    (cond
      (ready? match db)
      {:dispatch (or event (event-from-match-and-db match db))
       :clear-debounce debounce-key}

      (> retries max-on-visit-retries)
      {::error/capture-message-with-sentry ["Max on-visit retries exceeded" match]}

      :else
      {:dispatch-debounce {:key debounce-key
                           :event [::on-visit match debounce-key data (inc retries)]
                           :delay 50}})))

(rf/reg-cofx ::normalized-current-path-and-query
  (fn [cofx _]
    (assoc cofx :normalized-current-path-and-query
           (str
            (ensure-trailing-slash js/location.pathname)
            (adapt-legacy-filter-query js/location.search)
            js/location.hash))))

(rf/reg-fx ::history-replace-state
  (fn [new-url]
    (js/window.history.replaceState nil "" new-url)))

(defn- not-found-fx [url-properties path extra-data tags]
  (let [properties-404
        {:page (merge
                url-properties
                {:is_spa true
                 :section :error
                 :type :404})}]
    [[::tracking/register-super-properties properties-404]
     [::tracking/capture-pageview properties-404]
     [::error/capture-with-sentry {:type "ReititRouteError"
                                   :message (str "no match for path " path)
                                   :extra-data extra-data
                                   :tags tags}]
     [:dispatch [::events/navigate-to-page {:id :404}]]]))

(rf/reg-cofx ::navigation-diagnostics
  (fn [cofx _]
    (let [entry (first (js/performance.getEntriesByType "navigation"))]
      (assoc cofx :navigation-diagnostics
             {:navigation-type (some-> entry .-type)
              :transfer-size (some-> entry .-transferSize)
              :referrer-origin (some-> js/document.referrer not-empty js/URL. .-origin)
              :ms-since-load (js/Math.round (js/performance.now))}))))

(rf/reg-event-fx ::not-found-on-boot
  [(rf/inject-cofx ::tracking/url-properties)
   (rf/inject-cofx ::navigation-diagnostics)]
  (fn [{:keys [url-properties navigation-diagnostics]} [_ path reload-attempted?]]
    {:fx (not-found-fx url-properties path navigation-diagnostics
                       {:reload_attempted reload-attempted?})}))

;; WebKit sometimes boots this document under the URL of the Django page that redirected
;; to it, which no route matches; a reload lets the server redirect again (DEV-3823).
(def ^:private boot-reload-key "machtfit.app.routes.boot-reload-claimed-at")

;; A reload boots within seconds of its claim, so an older claim belongs to a boot that
;; ended elsewhere, on a Django page or a closed tab, and must not block this one.
(def ^:private boot-reload-claim-ms 30000)

(defn- claim-boot-reload! []
  (try
    (if (< (- (js/Date.now) (js/Number (.getItem js/sessionStorage boot-reload-key)))
           boot-reload-claim-ms)
      :attempted
      (do (.setItem js/sessionStorage boot-reload-key (js/Date.now))
          :claimed))
    (catch :default _
      :unavailable)))

(defn- forget-boot-reload! []
  (try
    (.removeItem js/sessionStorage boot-reload-key)
    (catch :default _ nil)))

(rf/reg-fx ::reload-once
  (fn [path]
    ;; Without the fragment, because a replace that differs only in one just scrolls.
    (let [url (str js/location.origin js/location.pathname js/location.search)]
      (case (claim-boot-reload!)
        :claimed (js/location.replace url)
        :attempted (do (forget-boot-reload!)
                       (rf/dispatch [::not-found-on-boot path true]))
        :unavailable (rf/dispatch [::not-found-on-boot path false])))))

(rf/reg-fx ::forget-boot-reload
  (fn [_]
    (forget-boot-reload!)))

(rf/reg-event-fx ::routing
  [(rf/inject-cofx ::normalized-current-path-and-query)
   (rf/inject-cofx ::tracking/url-properties)]
  (fn [{:keys [db
               normalized-current-path-and-query
               url-properties]}
       [_ router {:keys [boot?]}]]
    (when (:is-spa? db)
      (let [match (match-coerced router normalized-current-path-and-query)]
        (if (map? match)
          (let [{:keys [data]} match
                {:keys [event event-from-match-and-db on-visit]} data
                on-visit-events (for [[key data] on-visit]
                                  [::on-visit (assoc match :router router) key data 0])

                prev-page-id (get-in db [:ui :page :id])
                new-page-id (get-in data [:name :id])
                prev-on-leave (when (and prev-page-id
                                         (not= prev-page-id new-page-id))
                                (some-> (reitit/match-by-name router {:id prev-page-id})
                                        (get-in [:data :on-leave])))
                on-leave-events (map vec prev-on-leave)
                handler-event (if event-from-match-and-db
                                (event-from-match-and-db (assoc match :router router) db)
                                event)]

            {:fx (into (cond-> [[::history-replace-state normalized-current-path-and-query]]
                         boot? (conj [::forget-boot-reload]))
                       (map (fn [event] [:dispatch event]))
                       (concat on-leave-events [handler-event] on-visit-events))})
          {:fx (if (and boot? (nil? match))
                 [[::reload-once normalized-current-path-and-query]]
                 (cond-> (not-found-fx url-properties normalized-current-path-and-query nil nil)
                   boot? (conj [::forget-boot-reload])))})))))

(defn- server-renders? [db router href]
  (let [uri (goog.Uri. href)]
    (and (get-in db [:django-feature-flags global-search/cross-area-search-flag])
         (= :catalog (get-in (reitit/match-by-path router (.getPath uri)) [:data :name :id])))))

(rf/reg-event-fx ::navigate
  [(rf/inject-cofx ::normalized-current-path-and-query)]
  (fn [{:keys [db normalized-current-path-and-query]} [_ router {:keys [href scroll?] :or {scroll? true}}]]
    (let [tracking-properties (tracking/data-from-url router normalized-current-path-and-query)]
      (cond
        (server-renders? db router href)
        {:fx [[::tracking/capture-pageleave tracking-properties]]
         :redirect {:url href}}

        (renders-path? router (first (str/split href #"\?")))
        {:fx (cond-> [[::tracking/capture-pageleave tracking-properties]
                      [::tracking/unregister-super-properties #{:page}]
                      [::change-url-and-history href]
                      [:dispatch [::routing router]]]
               scroll? (conj [:scroll-to {:mode :page-top}]))}

        :else
        {:fx [[::load-page href]]}))))

(rf/reg-event-fx ::set-breadcrumbs
  (fn [{:keys [db]} [_ breadcrumbs]]
    (let [lacking-label (first (filter #(not (-> % :label :de)) breadcrumbs))]
      (if lacking-label
        {:db (assoc-in db [:ui :breadcrumbs] [])
         ::error/capture-with-sentry {:type "BreadcrumbMissingLabel"
                                      :message (str "breadcrumb for " (:href lacking-label) " has no label")
                                      :extra-data {:bad-breadcrumb lacking-label
                                                   :breadcrumbs breadcrumbs}}}
        {:db (assoc-in db [:ui :breadcrumbs] breadcrumbs)}))))

(defn default-tracking-function [match]
  {:page (get-in match [:data :tracking])})

(defn breadcrumb-by-route-name
  ([router db route-name]
   (breadcrumb-by-route-name router db {} route-name))
  ([router db path-params route-name]
   (let [{:keys [data] :as match}
         ;; matching by name does not coerce the path parameters
         ;; so in order to have them in the match we assoc them manually
         (assoc-in (reitit/match-by-name router route-name path-params)
                   [:parameters :path] path-params)
         from-match-and-db (get-in data [:breadcrumb :from-match-and-db])]
     (from-match-and-db match db))))

(defn serialize-tracking-page-data [tracking-page-data]
  (str/join "_"
            (keep #(when % (name %))
                  ((juxt :section :type :subtype) tracking-page-data))))

(defn default-breadcrumb-from-match-and-db [{:keys [path data] :as _match} _db]
  (let [{:keys [breadcrumb]} data
        {:keys [label]} breadcrumb]
    {:label label
     :id (serialize-tracking-page-data (:tracking data))
     :href path}))

(defn default-breadcrumb-event-from-match-and-db [{:keys [data router parameters] :as _match} db]
  (let [{route-name :name} data
        path-params (get parameters :path)
        parent-names (get-in data [:breadcrumb :parents])]
    [::set-breadcrumbs
     (mapv
      (partial breadcrumb-by-route-name router db path-params)
      (conj parent-names route-name))]))

(def routes
  (route-macros/validate-routes
   ["" {:coercion reitit.coercion.malli/coercion
        :event-from-match-and-db (fn [match _db]
                                   (get-in match [:data :event]))
        :breadcrumb {:from-match-and-db default-breadcrumb-from-match-and-db
                     :parents [{:id :home}]}
        :on-visit {:tracking {:ready? (constantly true)
                              :event-from-match-and-db (fn [match _db]
                                                         [::tracking/pageview
                                                          (default-tracking-function match)])}
                   :breadcrumbs {:ready? (constantly true)
                                 :event-from-match-and-db default-breadcrumb-event-from-match-and-db}
                   :set-title {:ready? (constantly true)
                               :event-from-match-and-db (fn [_match _db]
                                                          [::set-title "machtfit Übersicht – machtfit"])}}}
    ["/"

     ["" {:name {:id :home}
          :breadcrumb {:label {:de "Startseite"
                               :en "Home"}
                       :parents ^:replace []}
          :event []
          :tracking {:section :home
                     :type :home}}]

     ["stepchall/" {:tracking {:section :stepchall}
                    :breadcrumb {:from-match-and-db (fn [match db]
                                                      (assoc
                                                       (default-breadcrumb-from-match-and-db match db)
                                                       :label (get-in db [:user :stepchall-tile :name] {:de "Aktiv-Challenge"
                                                                                                        :en "Activity Challenge"})))}}
      ["" {:name {:id :stepchall}
           :tracking {:type :route}
           :event [::stepchall.route-view/navigate-to-stepchall-route]}]
      ["my-team/"
       ["" {:name {:id :stepchall
                   :subpage :my-team}
            :tracking {:type :my_team}
            :event [::stepchall.my-team/navigate-to-stepchall-my-team]}]
       ["members/" {:name {:id :stepchall
                           :subpage :members}
                    :tracking {:type :my_team
                               :subtype :my_team_members}
                    :event [::stepchall.my-team/navigate-to-stepchall-my-team {:show-members? true}]}]]
      ["create-team/" {:name {:id :stepchall
                              :subpage :create-team}
                       :tracking {:type :create_team}
                       :event [::stepchall.create-team/navigate-to-stepchall-create-team]}]
      ["my-steps/" {:name {:id :stepchall
                           :subpage :my-steps}
                    :tracking {:type :my_steps}
                    :event [::stepchall.my-steps/navigate-to-stepchall-my-steps]}]
      ["leaderboard/" {:name {:id :stepchall
                              :subpage :leaderboard}
                       :tracking {:type :leaderboard}
                       :event [::stepchall.leaderboard/navigate-to-stepchall-leaderboard]}]
      ["join-team/{team-id}/" {:name {:id :stepchall
                                      :subpage :join-team}
                               :tracking {:type :join_team}
                               :event-from-match-and-db (fn [match _db]
                                                          (let [team-id (get-in match [:path-params :team-id])
                                                                access-code (get-in match [:parameters :query :access-code])]
                                                            [::stepchall.join-team/navigate-to-stepchall-join-team
                                                             {:team-id (int team-id)
                                                              :team-access-code access-code}]))}]
      ["my-steps/select-activity/{entry-date}/" {:name {:id :stepchall
                                                        :subpage :select-activity}
                                                 :tracking {:type :select_activity}
                                                 :on-visit {:tracking {:event-from-match-and-db
                                                                       (fn [match _db]
                                                                         [::tracking/pageview
                                                                          (assoc (default-tracking-function match)
                                                                                 :entry_date (get-in match [:path-params :entry-date]))])}}
                                                 :event-from-match-and-db (fn [match _db]
                                                                            (let [entry-date (get-in match [:path-params :entry-date])]
                                                                              [::stepchall.add-activity/navigate-to-stepchall-select-activity
                                                                               {:id :stepchall
                                                                                :subpage :select-activity
                                                                                :entry-date (format/name->date entry-date)}]))}]
      ["my-steps/add-activity/{entry-date}/{activity}/" {:name {:id :stepchall
                                                                :subpage :add-activity}
                                                         :tracking {:type :add_activity}
                                                         :on-visit {:tracking {:event-from-match-and-db
                                                                               (fn [match _db]
                                                                                 [::tracking/capture-enriched-pageview
                                                                                  (-> match
                                                                                      default-tracking-function
                                                                                      (assoc :activity {:id (get-in match [:path-params :activity])})
                                                                                      (assoc :entry_date (get-in match [:path-params :entry-date])))])}}
                                                         :event-from-match-and-db (fn [match _db]
                                                                                    (let [{:keys [entry-date activity]} (get match :path-params)]
                                                                                      [::stepchall.add-activity/navigate-to-stepchall-select-activity
                                                                                       {:id :stepchall
                                                                                        :subpage :add-activity
                                                                                        :activity (keyword activity)
                                                                                        :entry-date (format/name->date entry-date)}]))}]]
     ["angebote/" {:name {:id :catalog}
                   :tracking {:section :catalogue
                              :type :offer_list}
                   :breadcrumb {:from-match-and-db
                                (fn [match {:keys [filter] :as db}]
                                  (assoc
                                   (default-breadcrumb-from-match-and-db match db)
                                   :label (case (:area filter)
                                            :company (-> db :ui :company-area-name)
                                            :machtfit {:de "machtfit-Angebote"
                                                       :en "machtfit offers"}
                                            {:de "Angebote"
                                             :en "Offers"})))}
                   :on-visit {:tracking {:event-from-match-and-db
                                         (fn [match _]
                                           (let [show-map? (url-params/url-param->boolean
                                                            (get-in match [:parameters :query :show-map] "false"))]
                                             [::tracking/pageview
                                              (assoc (default-tracking-function match)
                                                     :catalog {:view (if show-map? "map" "list")})]))}}
                   :event-from-match-and-db (fn [match _db]
                                              [::events/navigate-to-catalog nil
                                               (get-in match [:parameters :query])])}]
     ;; special cases outside the SPA. Used only to analyze the URL, hence the dummy event and no tracking
     ["checkout/place-order/{basket-id}/" {:name {:id :place-order}
                                           :event []
                                           :parameters {:path [:map
                                                               [:basket-id :int]]}}]
     ["checkout/thank-you/{basket-id}/" {:name {:id :checkout-thank-you}
                                         :event []
                                         :parameters {:path [:map
                                                             [:basket-id :int]]}}]

     ["basket/" {:name {:id :checkout}
                 :event []}]

     ["content/"
      ["magazin/" {:name {:id :magazine}
                   :event []}]
      ["magazin/tag/{slug}/" {:name {:id :magazine-list}
                              :event []
                              :parameters {:path [:map
                                                  [:slug :string]]}}]
      ["magazin/{slug}/" {:name {:id :magazine-detail}
                          :event []
                          :parameters {:path [:map
                                              [:slug :string]]}}]
      ["bgm-info/" {:name {:id :bgm-info}
                    :event []}]
      ["bgm-info/tag/{slug}/" {:name {:id :bgm-info-list}
                               :event []
                               :parameters {:path [:map
                                                   [:slug :string]]}}]
      ["bgm-info/{slug}/" {:name {:id :bgm-info-detail}
                           :event []
                           :parameters {:path [:map
                                               [:slug :string]]}}]]]]))

(def router
  (reitit/router routes {:compile reitit.coercion/compile-request-coercers}))

(defn keyword-set->url-param [value]
  (set (map name value)))

(defn int-set->url-param [value]
  (set (map str value)))

(def url-params-types
  {:sort-by name
   :area name
   :age keyword-set->url-param
   :gender keyword-set->url-param

   :weekdays int-set->url-param})

(defn converter [url-data]
  (set/rename-keys
   (into {}
         (map
          (fn [[k v]]
            (let [convert-fn (get url-params-types k identity)]
              [k (try
                   (convert-fn v)
                   (catch :default _
                     nil))])))
         url-data)
   url-params/url-param-names))

(defn page-url [{:keys [id
                        subpage
                        team-id
                        team-access-code
                        entry-date
                        activity
                        show-members?
                        filter-preset
                        filter
                        fragment
                        query-params]
                 :as page
                 :or {query-params {}}}]
  (let [default-fn #(reitit/match->path (reitit/match-by-name router (select-keys page [:id :subpage]) page))
        url (case id
              :catalog (reitit/match->path (reitit/match-by-name router
                                                                 {:id :catalog})
                                           (merge
                                            (cond
                                              (some? filter) (converter filter)
                                              filter-preset (converter (-> statics/filter-presets filter-preset :filter))
                                              :else {})
                                            query-params))
              :stepchall (case subpage
                           :my-team (if show-members?
                                      (reitit/match->path
                                       (reitit/match-by-name router {:id :stepchall
                                                                     :subpage :members})
                                       query-params)
                                      (reitit/match->path
                                       (reitit/match-by-name router {:id :stepchall
                                                                     :subpage :my-team})
                                       query-params))
                           :join-team (reitit/match->path (reitit/match-by-name router
                                                                                {:id :stepchall
                                                                                 :subpage :join-team}
                                                                                {:team-id team-id})
                                                          (when team-access-code {:access-code team-access-code}))
                           :select-activity (reitit/match->path
                                             (reitit/match-by-name router {:id :stepchall
                                                                           :subpage :select-activity} {:entry-date (format/date->name entry-date)})
                                             query-params)
                           :add-activity (reitit/match->path
                                          (reitit/match-by-name router {:id :stepchall
                                                                        :subpage :add-activity}
                                                                {:entry-date (format/date->name entry-date)
                                                                 :activity (name activity)})
                                          query-params)
                           nil (reitit/match->path
                                (reitit/match-by-name router {:id :stepchall})
                                query-params)
                           (default-fn))
              (default-fn))
        final-url (if fragment
                    (str url fragment)
                    url)]
    (when-not url
      (error/capture-with-sentry "ReititReverseRouteError" (str "Failed to generate path for route " (select-keys page [:id :subpage]))
                                 {:page-data page}))
    final-url))

(defn- on-link-click [{:keys [event href opts]}]
  (when event (.preventDefault event))
  (rf/dispatch [::navigate router {:href href}])
  (when (:scroll-to-top? opts)
    (rf/dispatch [::scrolling/to-page-top])))

(defn spa-path?
  "True when path matches a route the SPA renders."
  [path]
  (renders-path? router path))

(defn- attrs-link [{:keys [href opts]}]
  (cond-> {:href href}
    (and @(rf/subscribe [::init/is-spa?])
         (spa-path? (first (str/split href #"\?"))))
    (assoc :on-click #(on-link-click {:event %
                                      :href href
                                      :opts opts}))))

(defn attrs-link-to-page
  ([page]
   (attrs-link-to-page page {}))
  ([page opts]
   (attrs-link {:href (page-url page)
                :opts opts})))

(defn href-to-attrs
  "Like attrs-link-to-page but accepts a plain URL string instead of a route
  descriptor. If the path portion of href matches a SPA route, SPA navigation
  via the reitit router is used (preserving query params as-is). Otherwise a
  plain {:href href} map is returned, letting the browser perform a full-page
  navigation to the Django-served URL."
  ([href]
   (href-to-attrs href {}))
  ([href opts]
   (let [path (first (str/split href #"\?"))]
     (if (spa-path? path)
       (cond-> {:href href}
         @(rf/subscribe [::init/is-spa?])
         (assoc :on-click #(on-link-click {:event % :href href :opts opts})))
       {:href href}))))

(defn route-and-navigate-to-catalog [filter]
  [::navigate router {:href (reitit/match->path (reitit/match-by-name router {:id :catalog}) filter)}])

(defn route-and-navigate-to-page [page & {:keys [path-params query-params filter]}]
  [::navigate router {:href (reitit/match->path (reitit/match-by-name router page path-params)
                                                (merge query-params filter))}])
